import { create } from "zustand";
import type {
  TripSummaryWithPermission,
  CreateTripRequest,
} from "@poi-planner/shared";
import { api } from "../services/api";
import { daysBetweenDates } from "../utils/zonedTime";

interface TripsListState {
  // Data
  trips: TripSummaryWithPermission[];
  activeTripId: string | null;

  // UI state
  loading: boolean;
  error: string | null;

  // Actions
  loadTrips: () => Promise<void>;
  refreshTrips: () => Promise<void>;
  createTrip: (req: CreateTripRequest) => Promise<string>; // Returns new trip ID
  deleteTrip: (id: string) => Promise<void>;
  setActiveTripId: (id: string | null) => void;
  updateTripSummary: (summary: TripSummaryWithPermission) => void;

  // Helpers
  getActiveTripSummary: () => TripSummaryWithPermission | undefined;
}

export const useTripsListStore = create<TripsListState>((set, get) => ({
  trips: [],
  activeTripId: null,
  loading: true,
  error: null,

  loadTrips: async () => {
    set({ loading: true, error: null });
    try {
      const trips = await api.listTrips();
      set({ trips, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  refreshTrips: async () => {
    try {
      set({ trips: await api.listTrips(), error: null });
    } catch {
      // Keep the last successful trip list visible on a background refresh.
    }
  },

  createTrip: async (req: CreateTripRequest) => {
    set({ error: null });
    try {
      const data = await api.createNewTrip(req);
      const { trips } = get();

      // Compute summary from returned data
      const summary: TripSummaryWithPermission = {
        id: data.trip.id,
        name: data.trip.name,
        startDate: data.trip.startDate,
        endDate: data.trip.endDate,
        dayCount: daysBetweenDates(data.trip.startDate, data.trip.endDate) + 1,
        createdAt: data.trip.createdAt,
        updatedAt: data.trip.updatedAt,
        isOwner: true,
        permission: "owner",
      };

      set({ trips: [...trips, summary] });
      return data.trip.id;
    } catch (err) {
      set({ error: (err as Error).message });
      throw err;
    }
  },

  deleteTrip: async (id: string) => {
    set({ error: null });
    try {
      await api.deleteTripById(id);
      const { trips, activeTripId } = get();
      set({
        trips: trips.filter((t) => t.id !== id),
        activeTripId: activeTripId === id ? null : activeTripId,
      });
    } catch (err) {
      set({ error: (err as Error).message });
      throw err;
    }
  },

  setActiveTripId: (id: string | null) => {
    set({ activeTripId: id });
  },

  updateTripSummary: (summary: TripSummaryWithPermission) => {
    const { trips } = get();
    const idx = trips.findIndex((t) => t.id === summary.id);
    if (idx >= 0) {
      const newTrips = [...trips];
      newTrips[idx] = summary;
      set({ trips: newTrips });
    }
  },

  getActiveTripSummary: () => {
    const { trips, activeTripId } = get();
    return trips.find((t) => t.id === activeTripId);
  },
}));
